// Security configuration and monitoring component
import { useEffect } from 'react';
import { AuditService } from '@/services/auditService';
import { logger } from '@/utils/logger';

interface SecurityConfigProps {
  children: React.ReactNode;
}

const SecurityConfig = ({ children }: SecurityConfigProps) => {
  useEffect(() => {
    // Defer non-critical init work off the critical path. The audit RPC was
    // previously fired during initial render and showed up on Lighthouse's
    // critical request chain.
    const idle =
      (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
        .requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));

    const handle = idle(() => {
      AuditService.logUserAction('app_init', 'application').catch((err) => {
        logger.error('[SecurityConfig] Failed to log app init', err);
      });
    });

    // Monitor for suspicious activity (rapid automated clicking).
    // Debounced: at most one audit log per 30s window to avoid spamming the
    // audit table if a script keeps the click rate elevated.
    let clickCount = 0;
    let lastClickTime = Date.now();
    let lastAuditLoggedAt = 0;
    const AUDIT_COOLDOWN_MS = 30_000;

    const handleClick = () => {
      const now = Date.now();
      if (now - lastClickTime < 100) {
        clickCount++;
        if (clickCount > 20 && now - lastAuditLoggedAt > AUDIT_COOLDOWN_MS) {
          lastAuditLoggedAt = now;
          AuditService.logUserAction('suspicious_activity', 'security').catch((err) => {
            logger.error('[SecurityConfig] Failed to log suspicious activity', err);
          });
          clickCount = 0;
        }
      } else {
        clickCount = 0;
      }
      lastClickTime = now;
    };

    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
      const cancel = (window as unknown as { cancelIdleCallback?: (h: number) => void })
        .cancelIdleCallback;
      if (cancel) cancel(handle);
    };
  }, []);

  return <>{children}</>;
};

export default SecurityConfig;